import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import styles from './ModalTemplateMessage.module.scss';

interface VariableInputProps<FormType> {
  variableDisplay: string;
  fieldPath: Path<FormType>;
  control: Control<FormType>;
  setInputRef?: (element: HTMLInputElement | null) => void;
  required?: boolean;
  validationMessage?: string;
}

export const VariableInput = <FormType extends FieldValues>({
  variableDisplay,
  fieldPath,
  control,
  setInputRef,
  required = true,
  validationMessage,
}: VariableInputProps<FormType>) => {
  const { t } = useTranslation();
  // At the top rather than in a Controller render, so the compiler keeps the merged ref stable
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: fieldPath,
    rules: { required: required ? validationMessage || t('validation.required') : false },
  });

  return (
    <div className={cn(styles.buttonContainer, styles.withPrefix)}>
      <div className="-mr-0.5 flex h-10 w-fit items-center justify-center rounded-l rounded-tr-none rounded-br-none border border-border bg-muted p-2 text-sm text-ellipsis text-muted-foreground">
        {variableDisplay}
      </div>
      <Input
        aria-invalid={!!error}
        id={fieldPath as string}
        {...field}
        value={field.value ?? ''}
        ref={(e) => {
          field.ref(e);
          if (setInputRef) {
            setInputRef(e);
          }
        }}
      />
    </div>
  );
};
